import type { IScope } from "angular";
import { finalize } from "rxjs/operators";
import { cloneDeep, indexOf, isEmpty, isEqual } from "lodash";

import type {
    IAddress,
    IContactInformation,
    ICountry,
    IEmail,
    IPhoneNumber,
    IResponseWithLoading,
    ISite,
} from "App/Modules/Models";
import { AddressType, PhoneNumberType } from "App/Modules/Models";
import type { ICountriesRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type { IPhoneNumberTypeOption } from "./IPhoneNumberTypeOption";

export default class EditSiteContactInformationComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository", "irCountriesRepository", "$translate"];

    public site: ISite;

    public originalContactInformation: IContactInformation;

    public contactInformation: IContactInformation;

    public editResponse: IResponseWithLoading<ISite>;

    public addressTypes: string[];

    public phoneNumberTypes: IPhoneNumberTypeOption[];

    public countries: IResponseWithLoading<ICountry[]>;

    public partnerDefaultCountry: ICountry;

    public resolve: IFormContainerResolve<IContactInformation, ISite>;

    constructor(
        private readonly $scope: IScope,
        private readonly sitesRepository: ISitesRepository,
        private readonly countriesRepository: ICountriesRepository,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public $onInit() {
        this.site = this.resolve.bindings;

        this.originalContactInformation = this.resolve.formData;
        this.contactInformation = cloneDeep(this.originalContactInformation);

        const addressTypes: string[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const n in AddressType) {
            if (typeof AddressType[n] === "number") {
                addressTypes.push(n);
            }
        }
        addressTypes.sort();
        this.addressTypes = addressTypes;
        this.initializePhoneNumberTypes();

        extendWithLoadingAndFail(this.countriesRepository.getAllCountries())
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (response: IResponseWithLoading<ICountry[]>) => {
                    this.countries = response;

                    if (!response.isLoading && response.data) {
                        this.countries.data.forEach((c) => {
                            c.translated = this.$translate.instant(`Entities.Country.${c.alpha2}`);

                            if (c.id === this.site.partner.defaultCountryId && !this.partnerDefaultCountry) {
                                this.partnerDefaultCountry = c;
                            }
                        });
                    }
                },
                (error) => {
                    this.countries = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public addAddress() {
        this.contactInformation.addresses.push({
            address1: "",
            address2: "",
            addressType: AddressType[AddressType.General] as any,
            city: "",
            country: this.partnerDefaultCountry,
            zipCode: "",
        } as IAddress);
    }

    public addEmail() {
        this.contactInformation.emails.push({
            emailAddress: "",
        } as IEmail);
    }

    public addPhoneNumber() {
        this.contactInformation.phoneNumbers.push({
            number: "",
            phoneNumberType: PhoneNumberType[PhoneNumberType.Primary] as any,
        } as IPhoneNumber);
    }

    public deleteAddress(address: IAddress) {
        const index = indexOf(this.contactInformation.addresses, address);
        this.contactInformation.addresses.splice(index, 1);
    }

    public deleteEmail(email: IEmail) {
        const index = indexOf(this.contactInformation.emails, email);
        this.contactInformation.emails.splice(index, 1);
    }

    public deletePhoneNumber(phoneNumber: IPhoneNumber) {
        const index = indexOf(this.contactInformation.phoneNumbers, phoneNumber);
        this.contactInformation.phoneNumbers.splice(index, 1);
    }

    public editContactInformation() {
        const patchObject = {} as ISite;

        if (!isEqual(this.originalContactInformation, this.contactInformation)) {
            patchObject.contactInformation = this.contactInformation;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.sitesRepository.patchSite(this.site.id, patchObject)).subscribe(
                (response: IResponseWithLoading<ISite>) => {
                    this.editResponse = response;

                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data.contactInformation);
                    }
                },
                (errorResponse) => {
                    this.editResponse = errorResponse;
                },
            );
        } else {
            this.cancel();
        }
    }

    private initializePhoneNumberTypes() {
        const phoneNumberTypeTranslationKey = "Enums.PhoneNumberType.Enum";
        const phoneNumberTypes: IPhoneNumberTypeOption[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const option in PhoneNumberType) {
            if (typeof PhoneNumberType[option] === "number") {
                phoneNumberTypes.push({
                    Translation: this.$translate.instant(phoneNumberTypeTranslationKey + option),
                    Value: option,
                });
            }
        }
        phoneNumberTypes.sort((first, second) => {
            return first.Translation.localeCompare(second.Translation);
        });
        this.phoneNumberTypes = phoneNumberTypes;
    }
}
